import React, { useState } from 'react';
import { X, Copy, Share2, Link, Facebook, Linkedin, Mail, Code, QrCode, CheckCircle } from 'lucide-react';
import { Agent } from '../lib/supabase';
import { Twitter } from './Icons';

interface AgentShareModalProps {
  agent: Agent;
  isOpen: boolean;
  onClose: () => void;
}

export function AgentShareModal({ agent, isOpen, onClose }: AgentShareModalProps) {
  const [copied, setCopied] = useState<'link' | 'embed' | null>(null);
  const [activeTab, setActiveTab] = useState<'link' | 'embed' | 'social'>('link');

  if (!isOpen) return null;

  // Generate share URL
  const shareUrl = `${window.location.origin}/agent/${agent.id}`;
  
  // Generate embed code
  const embedCode = `<iframe src="${shareUrl}/embed" width="100%" height="500" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;

  const copyToClipboard = (text: string, type: 'link' | 'embed') => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  const shareOnSocial = (platform: string) => {
    let url = '';
    const text = `Check out this AI agent: ${agent.name} - ${agent.description}`;
    
    switch (platform) {
      case 'facebook':
        url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(text)}`;
        break;
      case 'twitter':
        url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`;
        break;
      case 'linkedin':
        url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
        break;
      case 'email':
        url = `mailto:?subject=${encodeURIComponent(`Check out this AI agent: ${agent.name}`)}&body=${encodeURIComponent(`${text}\n\n${shareUrl}`)}`;
        break;
    }
    
    if (url) window.open(url, '_blank');
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <Share2 className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Share Agent</h2>
              <p className="text-sm text-gray-600">{agent.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => setActiveTab('link')}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'link'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Link className="w-4 h-4" />
              Share Link
            </button>
            <button
              onClick={() => setActiveTab('embed')}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'embed'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Code className="w-4 h-4" />
              Embed
            </button>
            <button
              onClick={() => setActiveTab('social')}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'social'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Share2 className="w-4 h-4" />
              Social Media
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'link' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Share Link
                </label>
                <div className="flex">
                  <input
                    type="text"
                    value={shareUrl}
                    readOnly
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
                  />
                  <button
                    onClick={() => copyToClipboard(shareUrl, 'link')}
                    className="px-4 py-2 bg-blue-600 text-white rounded-r-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    {copied === 'link' ? (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Copied!
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="font-medium text-blue-900 mb-2">QR Code</h3>
                <div className="flex items-center justify-center p-4 bg-white rounded-lg border border-blue-100">
                  <div className="w-32 h-32 flex items-center justify-center bg-gray-100 rounded">
                    <QrCode className="w-16 h-16 text-gray-400" />
                    <span className="sr-only">QR Code for {agent.name}</span>
                  </div>
                </div>
                <p className="text-sm text-blue-700 mt-2 text-center">
                  Scan this QR code to share the agent
                </p>
              </div>
            </div>
          )}

          {activeTab === 'embed' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Embed Code
                </label>
                <div className="mb-4">
                  <textarea
                    value={embedCode}
                    readOnly
                    rows={4}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 font-mono text-sm"
                  />
                </div>
                <button
                  onClick={() => copyToClipboard(embedCode, 'embed')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  {copied === 'embed' ? (
                    <>
                      <CheckCircle className="w-4 h-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy Embed Code
                    </>
                  )}
                </button>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-medium text-gray-900 mb-2">Preview</h3>
                <div className="bg-white border border-gray-300 rounded-lg p-4 h-64 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Code className="w-8 h-8 text-gray-400" />
                    </div>
                    <p className="text-gray-600">Embedded {agent.name} preview</p>
                    <p className="text-sm text-gray-500 mt-2">
                      The actual embedded agent will appear here on your website
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'social' && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-4">
                  Share on Social Media
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <button
                    onClick={() => shareOnSocial('twitter')}
                    className="flex flex-col items-center gap-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Twitter className="w-8 h-8 text-blue-400" />
                    <span className="text-sm font-medium">Twitter</span>
                  </button>
                  <button
                    onClick={() => shareOnSocial('facebook')}
                    className="flex flex-col items-center gap-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Facebook className="w-8 h-8 text-blue-600" />
                    <span className="text-sm font-medium">Facebook</span>
                  </button>
                  <button
                    onClick={() => shareOnSocial('linkedin')}
                    className="flex flex-col items-center gap-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Linkedin className="w-8 h-8 text-blue-700" />
                    <span className="text-sm font-medium">LinkedIn</span>
                  </button>
                  <button
                    onClick={() => shareOnSocial('email')}
                    className="flex flex-col items-center gap-2 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    <Mail className="w-8 h-8 text-gray-600" />
                    <span className="text-sm font-medium">Email</span>
                  </button>
                </div>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <h3 className="font-medium text-purple-900 mb-2">Agent Signature</h3>
                <p className="text-sm text-purple-700 mb-4">
                  Add this signature to your website or email to showcase this agent:
                </p>
                <div className="bg-white border border-purple-100 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-lg flex items-center justify-center text-white">
                      <span className="text-lg font-bold">A</span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">{agent.name}</div>
                      <div className="text-xs text-gray-500">Powered by Agent Store</div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => copyToClipboard(`<div style="display:flex;align-items:center;gap:12px;padding:12px;border:1px solid #e5e7eb;border-radius:8px;font-family:system-ui,-apple-system,sans-serif;max-width:300px"><div style="width:40px;height:40px;background:linear-gradient(135deg,#2563eb,#9333ea);border-radius:8px;display:flex;align-items:center;justify-content:center;color:white"><span style="font-size:18px;font-weight:700">A</span></div><div><div style="font-weight:500;color:#111827">${agent.name}</div><div style="font-size:12px;color:#6b7280">Powered by Agent Store</div></div></div>`, 'embed')}
                  className="mt-3 px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors text-sm flex items-center gap-1"
                >
                  <Copy className="w-3 h-3" />
                  Copy Signature
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-500">
              Share this agent with your network
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}